import { useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Download, Share2, SquarePlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppSubShell, Button, Card, CardTitle } from '@up2d8/ui';

/**
 * "Zet UP2D8 op je beginscherm" (Stap 12).
 *
 * Deze pagina bestaat omdat installeren op elk besturingssysteem anders werkt en op geen enkel
 * systeem vanzelf gaat. En het is niet cosmetisch: op iOS werkt web push **alleen** in een app die op
 * het beginscherm staat, en zonder installatie start de app niet betrouwbaar zonder netwerk.
 *
 * Drie toestanden, en de app kiest zelf welke er staat:
 *
 * 1. **Al geïnstalleerd** — dan staat er dat het klaar is, en niets anders. Een instructie voor iets
 *    wat al gebeurd is, laat iemand twijfelen of hij het goed deed.
 * 2. **Android/Chrome** — de browser levert een echte installatieprompt aan. Eén knop.
 * 3. **iOS, of een browser zonder prompt** — dan de stappen, met de iconen die op het toestel zelf
 *    staan. "Deel" en "Zet op beginscherm" zijn de woorden die Apple gebruikt; iets anders schrijven
 *    zou een medewerker laten zoeken naar een knop die niet bestaat.
 */

interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export function AppInstallPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState<InstallPrompt | null>(null);
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    // De browser laat weten dat installeren kan. Wij houden het vast in plaats van het meteen te
    // tonen: een prompt die uit de lucht komt vallen, wordt weggeklikt (dezelfde les als bij
    // meldingen in Stap 9), en weggeklikt komt hij niet terug.
    const onAvailable = (event: Event) => {
      event.preventDefault();
      setPrompt(event as InstallPrompt);
    };
    const onInstalled = () => setInstalled(true);

    window.addEventListener('beforeinstallprompt', onAvailable);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onAvailable);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!prompt) return;
    await prompt.prompt();
    const choice = await prompt.userChoice;
    if (choice.outcome === 'accepted') setInstalled(true);
    setPrompt(null);
  };

  return (
    <AppSubShell title={t('install.title')} onBack={() => void navigate({ to: '/app' })}>
      <p className="text-content text-text-secondary">{t('install.why')}</p>

      {installed ? (
        <Card>
          <p className="flex items-start gap-2 text-content text-text-primary">
            <CheckCircle2 aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-success" />
            {t('install.alreadyInstalled')}
          </p>
        </Card>
      ) : prompt ? (
        <Card>
          <CardTitle>{t('install.androidTitle')}</CardTitle>
          <p className="mt-2 text-content text-text-secondary">{t('install.androidHint')}</p>
          <Button className="mt-element w-full" onClick={() => void install()}>
            <Download aria-hidden className="h-5 w-5" />
            {t('install.button')}
          </Button>
        </Card>
      ) : (
        <Card>
          <CardTitle>{isIos() ? t('install.iosTitle') : t('install.manualTitle')}</CardTitle>
          <ol className="mt-element flex flex-col gap-element">
            {(isIos()
              ? ([
                  { icon: Share2, text: t('install.iosStep1') },
                  { icon: SquarePlus, text: t('install.iosStep2') },
                  { icon: CheckCircle2, text: t('install.iosStep3') },
                ] as const)
              : ([
                  { icon: SquarePlus, text: t('install.manualStep1') },
                  { icon: CheckCircle2, text: t('install.manualStep2') },
                ] as const)
            ).map((step, index) => (
              <li key={index} className="flex items-start gap-element">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill bg-brand-primary-tint text-content font-semibold text-brand-primary">
                  {index + 1}
                </span>
                <span className="flex items-start gap-2 text-content text-text-primary">
                  <step.icon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-text-muted" />
                  {step.text}
                </span>
              </li>
            ))}
          </ol>
        </Card>
      )}

      <Card compact>
        <CardTitle>{t('install.offlineTitle')}</CardTitle>
        <ul className="mt-2 flex list-disc flex-col gap-2 pl-5 text-content text-text-secondary">
          <li>{t('install.offlineRead')}</li>
          <li>{t('install.offlineConfirm')}</li>
          <li>{t('install.offlineLimits')}</li>
        </ul>
      </Card>
    </AppSubShell>
  );
}
